// One job, as both the queue page and the footer's expanded list draw it.
//
// Shared because these two surfaces must never disagree about what a job is
// doing — a footer saying "running" over a page saying "failed" is worse than
// having no footer at all.

export type QueueJob = {
  readonly jobId: number;
  readonly kind: string;
  readonly label: string;
  readonly status: string;
  readonly phase: string | null | undefined;
  readonly currentObject: string | null | undefined;
  readonly rowsProcessed: number;
  readonly rowsTotal: number | null | undefined;
  readonly unit: string | null | undefined;
  readonly errorMessage: string | null | undefined;
  readonly params: unknown;
  readonly source: string;
  readonly requestedBy: string | null | undefined;
  readonly parentJobId: number | null | undefined;
  readonly supersededByJobId: number | null | undefined;
  readonly attempts: number;
  readonly startedAt: string | null | undefined;
  readonly finishedAt: string | null | undefined;
  readonly createdAt: string | null | undefined;
  readonly cancelRequestedAt: string | null | undefined;
  readonly stalled: boolean;
  readonly terminal: boolean;
  readonly queuePosition: number;
  readonly blocker: string | null | undefined;
  readonly stagesTotal: number;
  readonly stagesDone: ReadonlyArray<string>;
  readonly stageLabels: ReadonlyArray<{
    readonly key: string;
    readonly label: string;
    readonly state: string;
  }>;
  readonly outcome: ReadonlyArray<{
    readonly key: string;
    readonly value: string;
  }>;
  readonly typicalSeconds?: number | null | undefined;
  readonly etaSeconds?: number | null | undefined;
  readonly childJobIds?: ReadonlyArray<number> | null | undefined;
};

// What a job is actually doing, which is not always "working". The waiting
// states are separated from working because what the user can DO about them
// differs: queued behind a named job they can go and stop, versus alive but not
// moving, which they can only retry.
export type JobState =
  | "failed"
  | "superseded"
  | "stopped"
  | "done"
  | "stopping"
  | "queued"
  | "waiting"
  | "stalled"
  | "working";

export function stateOf(job: QueueJob): JobState {
  if (job.status === "cancelled") {
    return job.supersededByJobId ? "superseded" : "stopped";
  }
  if (job.status === "error") return "failed";
  if (job.status === "success") return "done";
  // Asked to stop, still running: the worker has not answered. Its own state,
  // because jumping to "stopped" while the worker still holds the account lock
  // is how a page comes to insist nothing is running while the next job waits.
  if (job.cancelRequestedAt) return "stopping";
  if (job.phase === "WAITING") return "waiting";
  if (job.status === "queued") return "queued";
  if (job.stalled) return "stalled";
  return "working";
}

export const STATE_CHIP: Record<
  JobState,
  { label: string; className: string }
> = {
  working: {
    label: "Running",
    className: "bg-blue-50 text-blue-700 border-blue-200",
  },
  queued: {
    label: "Queued",
    className: "bg-gray-50 text-gray-600 border-gray-200",
  },
  waiting: {
    label: "Waiting",
    className: "bg-amber-50 text-amber-800 border-amber-200",
  },
  stalled: {
    label: "Not moving",
    className: "bg-amber-50 text-amber-800 border-amber-200",
  },
  stopping: {
    label: "Stopping…",
    className: "bg-amber-50 text-amber-800 border-amber-200",
  },
  stopped: {
    label: "Stopped",
    className: "bg-gray-100 text-gray-600 border-gray-300",
  },
  superseded: {
    label: "Superseded",
    className: "bg-gray-100 text-gray-500 border-gray-300",
  },
  failed: {
    label: "Failed",
    className: "bg-red-50 text-red-700 border-red-200",
  },
  done: {
    label: "Done",
    className: "bg-green-50 text-green-700 border-green-200",
  },
};

const STAGE_MARK: Record<string, string> = {
  done: "✓",
  running: "●",
  pending: "○",
  skipped: "–",
};
const STAGE_CLASS: Record<string, string> = {
  done: "text-green-700",
  running: "font-medium text-blue-700",
  pending: "text-gray-400",
  skipped: "text-gray-300",
};

// A finished job's outcome as a lookup. The table stores it as key/value pairs
// because nothing kind-specific earns a column, so a caller that wants to write
// a sentence about a merge asks for the keys it knows.
export function outcomeOf(
  outcome: ReadonlyArray<{ readonly key: string; readonly value: string }>
): Record<string, string> {
  return Object.fromEntries(outcome.map((row) => [row.key, row.value]));
}

export function outcomeNumber(
  outcome: ReadonlyArray<{ readonly key: string; readonly value: string }>,
  key: string
): number {
  const raw = outcomeOf(outcome)[key];
  const parsed = raw == null ? NaN : Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

// Rounded to the unit a person would actually say out loud. "about 94 seconds"
// is precision nobody asked for and cannot act on.
export function humanDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

// What is LEFT while it runs, and what it USUALLY costs before it starts. The
// percentage says how far along something is; this says whether to wait for it.
// Silent when there is no history to answer from — a guess dressed as a
// measurement is worse than admitting we do not know.
export function timing(job: QueueJob): string | null {
  if (job.status === "running" && job.etaSeconds != null) {
    return `~${humanDuration(job.etaSeconds)} left`;
  }
  if (job.typicalSeconds == null) return null;
  if (job.status === "queued") return `usually ~${humanDuration(job.typicalSeconds)}`;
  // Running, but past its usual duration: saying "0 left" would be a lie, and
  // the elapsed time beside it already tells the true story.
  if (job.status === "running") return `usually ~${humanDuration(job.typicalSeconds)}`;
  return null;
}

export function elapsed(job: QueueJob): string | null {
  const from = job.startedAt ?? job.createdAt;
  if (!from) return null;
  const to = job.finishedAt ? new Date(job.finishedAt) : new Date();
  const seconds = Math.max(0, (to.getTime() - new Date(from).getTime()) / 1000);
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  return minutes < 60
    ? `${minutes} min`
    : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export default function JobCard({
  job,
  busy,
  onStop,
  onRetry,
  compact = false,
}: {
  job: QueueJob;
  busy: boolean;
  onStop: (jobId: number) => void;
  onRetry: (jobId: number) => void;
  compact?: boolean;
}) {
  const state = stateOf(job);
  const chip = STATE_CHIP[state];
  const done = job.stagesDone.length;

  // Two bars would be two lies. Steps finished is the honest measure of a job;
  // the counter inside a step is what that step is doing right now, and belongs
  // on its own line with its own unit rather than folded into one percentage
  // that pretends every step takes the same time.
  const pct =
    job.stagesTotal > 0 ? Math.round((done / job.stagesTotal) * 100) : null;
  const within =
    job.rowsTotal && job.rowsTotal > 0
      ? Math.min(100, Math.round((job.rowsProcessed / job.rowsTotal) * 100))
      : null;

  return (
    <div
      className={`rounded border border-gray-200 bg-white ${
        compact ? "p-3" : "p-4"
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`rounded border px-2 py-0.5 text-xs font-medium ${chip.className}`}
        >
          {chip.label}
        </span>
        <span className="text-sm font-medium text-gray-800">{job.label}</span>
        <span className="text-xs text-gray-400">#{job.jobId}</span>
        {job.source === "cascade" && job.parentJobId && (
          <span
            className="rounded bg-gray-50 px-1.5 py-0.5 text-xs text-gray-500"
            title="Queued automatically because job #{job.parentJobId} made it necessary"
          >
            after #{job.parentJobId}
          </span>
        )}
        {state === "queued" && job.queuePosition > 1 && (
          <span className="text-xs text-gray-500">
            {job.queuePosition - 1} ahead of it
          </span>
        )}
        {job.attempts > 1 && (
          <span className="text-xs text-gray-500">attempt {job.attempts}</span>
        )}
        <span className="ml-auto text-xs text-gray-400">
          {job.stagesTotal > 0
            ? `${done} of ${job.stagesTotal} steps`
            : chip.label}
          {elapsed(job) ? ` · ${elapsed(job)}` : ""}
          {timing(job) ? ` · ${timing(job)}` : ""}
        </span>
      </div>

      {pct != null && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-gray-100">
          <div
            className={`h-full transition-all ${
              state === "failed"
                ? "bg-red-400"
                : state === "done"
                ? "bg-green-500"
                : "bg-blue-500"
            }`}
            style={{ width: `${state === "done" ? 100 : pct}%` }}
          />
        </div>
      )}

      {!job.terminal && (
        <p className="mt-2 text-sm text-gray-700">
          {job.currentObject ?? "Starting…"}
          {within != null && (
            <span className="ml-2 font-mono text-xs tabular-nums text-gray-500">
              {job.rowsProcessed.toLocaleString()}
              {job.rowsTotal ? ` / ${job.rowsTotal.toLocaleString()}` : ""}
              {job.unit ? ` ${job.unit}` : ""} ({within}%)
            </span>
          )}
        </p>
      )}

      {state === "waiting" && job.blocker && (
        <p className="mt-1 text-xs text-amber-800">
          Waiting for {job.blocker}. It starts on its own once that finishes.
        </p>
      )}

      {state === "stalled" && (
        <p className="mt-1 text-xs text-amber-800">
          Alive but nothing has moved for a while. It may be inside one long
          database statement — or wedged, in which case stopping and retrying
          resumes from the last finished step.
        </p>
      )}

      {job.errorMessage && (
        <p
          className={`mt-2 rounded px-2 py-1.5 text-xs ${
            state === "failed"
              ? "bg-red-50 text-red-700"
              : "bg-gray-50 text-gray-600"
          }`}
        >
          {job.errorMessage}
        </p>
      )}

      {!compact && job.stageLabels.length > 0 && (
        <ul className="mt-3 space-y-1">
          {job.stageLabels.map((stage) => (
            <li
              key={stage.key}
              className={`text-xs ${STAGE_CLASS[stage.state] ?? ""}`}
            >
              <span className="mr-1.5 font-mono">
                {STAGE_MARK[stage.state] ?? "○"}
              </span>
              {stage.label}
            </li>
          ))}
        </ul>
      )}

      {!compact && job.terminal && job.outcome.length > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          {job.outcome.map((row) => `${row.key} ${row.value}`).join(" · ")}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!job.terminal && (
          <button
            type="button"
            onClick={() => onStop(job.jobId)}
            disabled={busy}
            className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            {state === "stopping" ? "Stop now" : "Stop"}
          </button>
        )}
        {job.terminal && job.status !== "success" && !job.supersededByJobId && (
          <button
            type="button"
            onClick={() => onRetry(job.jobId)}
            disabled={busy}
            className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            {done > 0 ? `Retry — resumes from step ${done + 1}` : "Retry"}
          </button>
        )}
        {!compact && job.requestedBy && (
          <span className="text-xs text-gray-400">
            asked for by {job.requestedBy}
          </span>
        )}
        {!compact && job.source === "cascade" && (
          <span className="text-xs text-gray-400">queued automatically</span>
        )}
      </div>
    </div>
  );
}
